import { useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { API, getToken } from "@/lib/api";

const KATEGORI = [
  {
    key: "biodata_umum",
    label: "Biodata Umum",
    desc: "NIK, tempat/tgl lahir, jenis kelamin, alamat, pendidikan, dan status pernikahan.",
  },
  {
    key: "jabatan_penempatan",
    label: "Jabatan & Penempatan",
    desc: "Unit kerja, lokasi, jabatan, status kepegawaian, bidang kerja, kode pangkat, tingkat jabatan.",
  },
  {
    key: "komponen_gaji",
    label: "Komponen Gaji",
    desc: "Gaji pokok, NPWP, PTKP, bank, nomor rekening, cost center, koperasi, kuota cuti.",
  },
  {
    key: "komponen_upah",
    label: "Rincian Komponen Upah",
    desc: "Satu kolom per komponen upah aktif (tunjangan, potongan) dengan nilai per pegawai.",
  },
  {
    key: "data_kontrak",
    label: "Data Kontrak",
    desc: "Awal dan akhir masa kontrak kerja pegawai.",
  },
  {
    key: "bpjs",
    label: "Data BPJS",
    desc: "Nomor BPJS Ketenagakerjaan dan BPJS Kesehatan.",
  },
  {
    key: "data_akun",
    label: "Data Akun",
    desc: "Email, role, status akun, dan rantai atasan 1/2/3 di sistem.",
  },
];

export function ExportPegawaiDialog({ open, onOpenChange }) {
  const [selected, setSelected] = useState({
    biodata_umum: true,
    jabatan_penempatan: true,
    komponen_gaji: true,
    komponen_upah: true,
    data_kontrak: true,
    bpjs: true,
    data_akun: true,
  });
  const [working, setWorking] = useState(false);

  function toggle(key) {
    setSelected((s) => ({ ...s, [key]: !s[key] }));
  }

  async function doExport() {
    const categories = Object.keys(selected).filter((k) => selected[k]);
    if (categories.length === 0) {
      toast.error("Pilih minimal satu kategori data");
      return;
    }
    setWorking(true);
    try {
      const token = getToken();
      const resp = await fetch(`${API}/admin/pegawai/export-xlsx`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ categories }),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "Gagal export");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `DaftarPegawai_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Data pegawai berhasil diekspor");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || "Gagal export");
    } finally {
      setWorking(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="export-pegawai-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-emerald-700" />
            Export Data Pegawai ke Excel
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pilih kategori data yang ingin disertakan dalam file Excel. Hanya kolom yang
            dicentang yang akan tampil pada file hasil export.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {KATEGORI.map((k) => (
            <label
              key={k.key}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                selected[k.key] ? "bg-teal-50 border-teal-200" : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`kategori-${k.key}`}
            >
              <Checkbox
                checked={!!selected[k.key]}
                onCheckedChange={() => toggle(k.key)}
                className="mt-0.5"
                data-testid={`kategori-check-${k.key}`}
              />
              <div>
                <div className="font-semibold text-sm text-slate-900">{k.label}</div>
                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{k.desc}</div>
              </div>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={working}>Batal</Button>
          <Button
            onClick={doExport}
            disabled={working}
            className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
            data-testid="export-pegawai-confirm-btn"
          >
            <FileSpreadsheet size={14} />
            {working ? "Mengekspor…" : "Download Excel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
